import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../utils/axios';
import { toastSuccess, toastError } from '../../../utils/toast';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import useConfirmModal from '../../../components/ConfirmModal/useConfirmModal';
import useLeaseGate from '../../../components/LeaseAgreementGate/useLeaseGate';
import LeaseAgreementGate from '../../../components/LeaseAgreementGate/LeaseAgreementGate';
import { bulkDraftReady, getListingName } from '../groupings';
import { routeForEditFromDrawer } from '../routing';
import { isBulkDraftListing } from './drawerEditHelpers';
import PauseListingModal from './PauseListingModal/PauseListingModal';

// PP-403 — turn the submit endpoint's structured 422 response into a
// human-readable toast. The server returns one of:
//   { error: 'listing not ready for submission', readiness: { hasIngress: false, ... } }
//   { error: 'insurance not addressed', missing: ['insurance'] }
//   { error: 'lease acceptance required' }
// Falls back to a generic message when the shape doesn't match.
const READINESS_LABELS = {
    enrichmentComplete: 'AI enrichment still running',
    hasAddress: 'address missing',
    hasConfirmedFields: 'details missing',
    hasIngress: 'entrance markers missing',
    hasEgress: 'exit markers missing',
    availabilityValid: 'availability has overlapping time blocks',
};
function formatSubmitError(err) {
    const data = err?.response?.data;
    const msg = data?.error;
    if (msg === 'listing not ready for submission' && data?.readiness) {
        const missing = Object.entries(data.readiness)
            .filter(([, ok]) => !ok)
            .map(([k]) => READINESS_LABELS[k] || k);
        if (missing.length) {
            return `Can’t submit yet: ${missing.join(', ')}.`;
        }
    }
    if (msg === 'insurance not addressed') {
        return 'Can’t submit yet: insurance answer required (go to the Insurance step in the wizard).';
    }
    if (msg === 'lease acceptance required' || msg === 'invalid lease version') {
        return 'Lease acceptance required. Refresh and try again.';
    }
    if (msg) return `Couldn’t submit: ${msg}`;
    return 'Couldn’t submit for review. Please try again.';
}

export default function DrawerFooter({
    listing,
    onClose,
    onSubmitted,
    editMode = false,
    isLiveEdit = false,
    canLiveEdit = false,
    onEnterEditMode,
    onSaveEdits,
    onCancelEdits,
    saving = false,
    hasDirty = false,
}) {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [pauseModalOpen, setPauseModalOpen] = useState(false);
    const [pauseBusy, setPauseBusy] = useState(false);
    const discardModal = useConfirmModal();
    const withdrawModal = useConfirmModal();
    const resumeModal = useConfirmModal();
    const leaseGate = useLeaseGate();
    const v5 = listing._v5Status;
    const isBulkDraft = isBulkDraftListing(listing);

    const listingName = getListingName(listing);

    const goEdit = (withEditFlag = false) => {
        if (isBulkDraft) {
            onEnterEditMode?.();
            return;
        }
        navigate(routeForEditFromDrawer(listing, { edit: withEditFlag || v5 === 'attention' }));
    };

    const handleSubmitDraft = async () => {
        if (!bulkDraftReady(listing) || submitting) return;
        setSubmitting(true);
        try {
            await leaseGate.runWithLeaseGate({
                listings: [listing],
                onSuccess: () => {
                    onSubmitted?.();
                    onClose();
                },
            });
        } catch {
            // keep drawer open on failure
        } finally {
            setSubmitting(false);
        }
    };

    // PP-403 — submit a paused-with-edits listing for admin review. The
    // server's POST /listings/:id/submit accepts paused-resubmits (it
    // checks `pausedAt && pausedHasEdits` in addition to the standard
    // SUBMITTABLE_STATUSES). Listing was already approved once so all
    // readiness checks (photos, insurance, lease) should pass; if any
    // don't, the server returns 422 and we surface its message so the
    // host knows what to fix instead of a generic "try again" toast.
    // We deliberately reuse the same lease-gate path because the
    // paused-resubmit re-accepts the current lease version if it's
    // changed since the original approval.
    const handleSubmitPausedResubmit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await leaseGate.runWithLeaseGate({
                listings: [listing],
                onSuccess: () => {
                    toastSuccess('Submitted for review — we\'ll notify you when our team is done.');
                    onSubmitted?.();
                    onClose();
                },
                onError: (err) => {
                    toastError(formatSubmitError(err));
                },
            });
        } catch {
            // keep drawer open on failure
        } finally {
            setSubmitting(false);
        }
    };

    const handleDiscard = () => {
        const name = listingName || 'this draft';

        discardModal.confirm({
            title: 'Discard this draft?',
            message: `"${name}" will be removed from your drafts. This cannot be undone.`,
            confirmLabel: 'Discard draft',
            cancelLabel: 'Keep editing',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await axios.delete(`/listings/${listing._id}`);
                    toastSuccess('Draft discarded');
                    onSubmitted?.();
                    onClose();
                } catch {
                    toastError('Couldn\'t discard draft. Please try again.');
                }
            },
        });
    };

    const handleWithdraw = () => {
        withdrawModal.confirm({
            title: 'Withdraw submission?',
            message: `"${listingName}" will return to your drafts. You'll be able to edit and resubmit at any time.`,
            confirmLabel: 'Withdraw',
            cancelLabel: 'Keep in review',
            variant: 'default',
            onConfirm: async () => {
                try {
                    await axios.post(`/listings/${listing._id}/withdraw`);
                    toastSuccess('Submission withdrawn — your listing is back in drafts.');
                    onSubmitted?.();
                    onClose();
                } catch {
                    toastError('Couldn\'t withdraw. Please try again.');
                }
            },
        });
    };

    const handlePauseClick = () => {
        setPauseModalOpen(true);
    };

    const handlePauseConfirm = async (reason) => {
        if (pauseBusy) return;
        setPauseBusy(true);
        try {
            await axios.post(`/listings/${listing._id}/pause`, { reason: reason || undefined });
            toastSuccess('Listing paused.');
            setPauseModalOpen(false);
            onSubmitted?.();
            onClose();
        } catch {
            toastError('Couldn\'t pause listing. Please try again.');
        } finally {
            setPauseBusy(false);
        }
    };

    const handleResume = () => {
        resumeModal.confirm({
            title: 'Resume this listing?',
            message: 'Your listing will reappear in search and accept new bookings.',
            confirmLabel: 'Resume',
            cancelLabel: 'Cancel',
            variant: 'default',
            onConfirm: async () => {
                try {
                    await axios.post(`/listings/${listing._id}/resume`);
                    toastSuccess('Listing resumed.');
                    onSubmitted?.();
                    onClose();
                } catch {
                    toastError('Couldn\'t resume listing. Please try again.');
                }
            },
        });
    };

    if (editMode && (isBulkDraft || isLiveEdit)) {
        return (
            <footer className="host-panel-footer">
                <button type="button" className="btn btn-secondary" onClick={onCancelEdits}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSaveEdits}
                    disabled={saving || !hasDirty}
                >
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
            </footer>
        );
    }

    const submitReady = bulkDraftReady(listing);
    const submitTitle = !submitReady
        ? 'Confirm geofence and AI fields before submitting'
        : undefined;

    let buttons = (
        <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
        </button>
    );

    if (v5 === 'live') {
        buttons = (
            <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePauseClick}
                >
                    Pause listing
                </button>
                {canLiveEdit && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onEnterEditMode}
                    >
                        Edit listing
                    </button>
                )}
            </>
        );
    } else if (v5 === 'paused') {
        // PP-403 — paused with NO unreviewed edits. Host can resume
        // directly (no admin involvement) or jump into the SPW to make
        // changes (which would flip them into the paused_review branch
        // below on save).
        buttons = (
            <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button type="button" className="btn btn-secondary" onClick={() => goEdit(false)}>
                    Edit listing →
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleResume}
                >
                    Resume listing
                </button>
            </>
        );
    } else if (v5 === 'paused_review') {
        // PP-403 — paused with unreviewed edits. Resume is disabled;
        // host must Submit for Review (status → 'pending', joins the
        // normal admin queue) or keep editing.
        buttons = (
            <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button type="button" className="btn btn-secondary" onClick={() => goEdit(false)}>
                    Edit listing →
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmitPausedResubmit}
                    disabled={submitting || leaseGate.gateProps.open}
                >
                    {submitting || leaseGate.gateProps.busy ? 'Submitting…' : 'Submit for review'}
                </button>
            </>
        );
    } else if (v5 === 'review') {
        buttons = (
            <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleWithdraw}
                >
                    Withdraw
                </button>
            </>
        );
    } else if (v5 === 'attention') {
        buttons = (
            <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button type="button" className="btn btn-primary" onClick={() => goEdit(true)}>
                    Edit &amp; Resubmit →
                </button>
            </>
        );
    } else if (v5 === 'draft') {
        buttons = (
            <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleDiscard}
                >
                    Discard draft
                </button>
                <button type="button" className="btn btn-primary" onClick={() => goEdit(false)}>
                    Resume draft →
                </button>
            </>
        );
    } else if (v5 === 'bulk_draft') {
        buttons = (
            <>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleDiscard}
                >
                    Discard
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!submitReady || submitting || leaseGate.gateProps.open}
                    title={submitTitle}
                    onClick={handleSubmitDraft}
                >
                    {submitting || leaseGate.gateProps.busy ? 'Submitting…' : 'Submit this draft →'}
                </button>
            </>
        );
    }

    return (
        <>
            <footer className="host-panel-footer">
                {buttons}
            </footer>
            <PauseListingModal
                open={pauseModalOpen}
                onClose={() => !pauseBusy && setPauseModalOpen(false)}
                onConfirm={handlePauseConfirm}
                listingName={listingName}
                busy={pauseBusy}
            />
            <ConfirmModal {...discardModal.props} />
            <ConfirmModal {...withdrawModal.props} />
            <ConfirmModal {...resumeModal.props} />
            <LeaseAgreementGate {...leaseGate.gateProps} />
        </>
    );
}
